import { useEffect, useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Loader2, Trash2, Plus, Users, FileText, Briefcase, MessageCircle, ShoppingCart } from "lucide-react";
import SEO from "@/components/SEO";
import { SEO_MAP } from "@/lib/seoMap";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "orders", label: "Orders" },
  { key: "leads", label: "Leads" },
  { key: "blog", label: "Blog" },
  { key: "portfolio", label: "Portfolio" },
  { key: "testimonials", label: "Testimonials" },
  { key: "faqs", label: "FAQs" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");

  return (
    <main className="min-h-[100svh] pt-24 pb-24 relative" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="overline mb-2">Admin CMS</div>
            <h1 className="font-serif text-4xl md:text-5xl tracking-[-0.02em]">
              LaneX Console<span className="italic text-brand-primary">.</span>
            </h1>
            <p className="text-[13.5px] text-[color:var(--text-muted)] mt-1">Signed in as {user?.email}</p>
          </div>
          <button
            onClick={logout}
            className="h-10 px-4 rounded-full border border-[color:var(--border-strong)] text-[13px] font-medium inline-flex items-center gap-2"
            data-testid="admin-logout"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-[color:var(--border)] mb-8">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`h-10 px-4 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-brand-primary text-[color:var(--text)]" : "border-transparent text-[color:var(--text-muted)] hover:text-[color:var(--text)]"
              }`}
              data-testid={`admin-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && <Overview />}
        {tab === "orders" && <OrdersPanel />}
        {tab === "leads" && <LeadsPanel />}
        {tab === "blog" && <BlogPanel />}
        {tab === "portfolio" && <PortfolioPanel />}
        {tab === "testimonials" && <TestimonialsPanel />}
        {tab === "faqs" && <FaqsPanel />}
      </div>
    </main>
  );
}

// ---------------- Overview ----------------
function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  if (!stats) return <div className="py-10"><Loader2 className="animate-spin" /></div>;
  const cards = [
    { label: "Leads", value: stats.leads, icon: MessageCircle },
    { label: "Orders", value: `${stats.orders} (${stats.orders_open} open)`, icon: ShoppingCart },
    { label: "Users", value: stats.users, icon: Users },
    { label: "Blog Posts", value: stats.blog_posts, icon: FileText },
    { label: "Portfolio", value: stats.portfolio, icon: Briefcase },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="card-surface p-6" data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="flex items-center gap-2 mb-4"><c.icon size={14} className="text-brand-primary" /><span className="overline">{c.label}</span></div>
            <div className="font-serif text-3xl tracking-tight">{c.value}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-8">
        <div className="card-surface p-6">
          <div className="overline mb-4">Recent Leads</div>
          <div className="divide-y divide-[color:var(--border)]">
            {stats.recent_leads.map((l) => (
              <div key={l.id} className="py-3 flex items-center justify-between text-[13.5px]">
                <div>
                  <div className="font-medium">{l.business_name} · {l.contact_name}</div>
                  <div className="text-[12px] text-[color:var(--text-muted)]">{l.service_type} · {l.budget}</div>
                </div>
                <div className="text-[12px] font-mono text-[color:var(--text-muted)]">{new Date(l.created_at).toLocaleDateString()}</div>
              </div>
            ))}
            {!stats.recent_leads.length && <div className="py-6 text-[13px] text-[color:var(--text-muted)]">No leads yet.</div>}
          </div>
        </div>
        <div className="card-surface p-6">
          <div className="overline mb-4">Recent Orders</div>
          <div className="divide-y divide-[color:var(--border)]">
            {(stats.recent_orders || []).map((o) => (
              <div key={o.id} className="py-3 flex items-center justify-between text-[13.5px]">
                <div>
                  <div className="font-medium">{o.order_number} · {o.contact_name}</div>
                  <div className="text-[12px] text-[color:var(--text-muted)]">{o.items.map((i) => i.label).join(", ")}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono">£{o.total_gbp.toFixed(2)}</div>
                  <div className="text-[11px] text-[color:var(--text-muted)] uppercase tracking-widest">{o.status}</div>
                </div>
              </div>
            ))}
            {!(stats.recent_orders || []).length && <div className="py-6 text-[13px] text-[color:var(--text-muted)]">No orders yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Leads ----------------
function LeadsPanel() {
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = () => api.get("/leads").then(({ data }) => setLeads(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    await api.delete(`/leads/${id}`); toast.success("Lead deleted"); setSelected(null); load();
  };
  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 md:col-span-5 card-surface p-4 max-h-[70vh] overflow-auto">
        {leads.map((l) => (
          <button key={l.id} onClick={() => setSelected(l)}
                  className={`w-full text-left p-4 rounded-xl mb-1 border transition-colors ${selected?.id === l.id ? "border-brand-primary bg-brand-primary/5" : "border-transparent hover:bg-[color:var(--surface-elev)]"}`}
                  data-testid={`lead-item-${l.id}`}>
            <div className="font-medium text-[13.5px]">{l.business_name}</div>
            <div className="text-[12px] text-[color:var(--text-muted)]">{l.service_type} · {l.budget}</div>
          </button>
        ))}
        {!leads.length && <div className="p-6 text-[13px] text-[color:var(--text-muted)]">No leads yet.</div>}
      </div>
      <div className="col-span-12 md:col-span-7 card-surface p-6 min-h-[300px]">
        {selected ? (
          <div>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline">{selected.service_type}</div>
                <h3 className="font-serif text-2xl mt-1">{selected.business_name}</h3>
              </div>
              <button onClick={() => del(selected.id)} className="text-red-500 h-9 px-3 rounded-lg border border-red-500/30 hover:bg-red-500/10 text-[12px] inline-flex items-center gap-1.5" data-testid="lead-delete">
                <Trash2 size={13} /> Delete
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4 text-[13px]">
              <Field k="Contact" v={selected.contact_name} />
              <Field k="Email" v={selected.contact_email} />
              <Field k="Phone" v={selected.contact_phone} />
              <Field k="Company" v={selected.contact_company || "—"} />
              <Field k="Industry" v={selected.industry} />
              <Field k="Country" v={selected.country} />
              <Field k="Budget" v={selected.budget} />
              <Field k="Timeline" v={selected.timeline} />
            </div>
            <div className="mt-6">
              <div className="overline mb-2">Project Details</div>
              <p className="text-[14px] leading-relaxed whitespace-pre-wrap">{selected.project_details}</p>
            </div>
          </div>
        ) : (
          <div className="text-[13.5px] text-[color:var(--text-muted)]">Select a lead to view details.</div>
        )}
      </div>
    </div>
  );
}
function Field({ k, v }) { return <div><div className="overline mb-1">{k}</div><div>{v}</div></div>; }

// ---------------- Orders ----------------
const ORDER_STATUSES = ["reserved", "invoiced", "paid", "provisioned", "cancelled"];

function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = () => api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    await api.delete(`/orders/${id}`); toast.success("Order deleted"); setSelected(null); load();
  };
  const setStatus = async (id, status) => {
    await api.patch(`/orders/${id}`, { status });
    toast.success(`Status → ${status}`);
    setSelected((s) => (s ? { ...s, status } : s));
    load();
  };
  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 md:col-span-5 card-surface p-4 max-h-[70vh] overflow-auto">
        {orders.map((o) => (
          <button key={o.id} onClick={() => setSelected(o)}
                  className={`w-full text-left p-4 rounded-xl mb-1 border transition-colors ${selected?.id === o.id ? "border-brand-primary bg-brand-primary/5" : "border-transparent hover:bg-[color:var(--surface-elev)]"}`}
                  data-testid={`order-item-${o.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-mono text-[12.5px]">{o.order_number}</div>
              <div className="font-mono text-[12px]">£{o.total_gbp.toFixed(2)}</div>
            </div>
            <div className="mt-1 flex items-center justify-between">
              <div className="text-[12px] text-[color:var(--text-muted)]">{o.contact_name}</div>
              <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--text-muted)]">{o.status}</div>
            </div>
          </button>
        ))}
        {!orders.length && <div className="p-6 text-[13px] text-[color:var(--text-muted)]">No orders yet.</div>}
      </div>
      <div className="col-span-12 md:col-span-7 card-surface p-6 min-h-[300px]">
        {selected ? (
          <div>
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="overline">Order</div>
                <h3 className="font-serif text-2xl mt-1">{selected.order_number}</h3>
              </div>
              <button onClick={() => del(selected.id)} className="text-red-500 h-9 px-3 rounded-lg border border-red-500/30 hover:bg-red-500/10 text-[12px] inline-flex items-center gap-1.5" data-testid="order-delete">
                <Trash2 size={13} /> Delete
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4 text-[13px] mb-6">
              <Field k="Customer" v={selected.contact_name} />
              <Field k="Email" v={selected.contact_email} />
              <Field k="Phone" v={selected.contact_phone} />
              <Field k="Company" v={selected.contact_company || "—"} />
            </div>
            <div className="mb-6">
              <div className="overline mb-2">Items</div>
              <div className="divide-y divide-[color:var(--border)] rounded-xl border border-[color:var(--border)]">
                {selected.items.map((it, i) => (
                  <div key={i} className="p-3 flex items-center justify-between text-[13px]">
                    <div>
                      <div className="font-medium">{it.label}{it.qty > 1 && ` × ${it.qty}`}</div>
                      <div className="text-[11px] text-[color:var(--text-muted)] font-mono uppercase tracking-widest">{it.kind} · {it.sku}</div>
                    </div>
                    <div className="font-mono">£{it.line_total_gbp.toFixed(2)}</div>
                  </div>
                ))}
                <div className="p-3 flex items-center justify-between text-[14px]">
                  <span className="overline">Total</span>
                  <span className="font-serif text-xl">£{selected.total_gbp.toFixed(2)}</span>
                </div>
              </div>
            </div>
            <div>
              <div className="overline mb-2">Status</div>
              <div className="flex flex-wrap gap-1.5">
                {ORDER_STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatus(selected.id, s)}
                    className={`h-9 px-3 rounded-lg text-[12px] font-medium border transition-colors ${selected.status === s ? "border-brand-primary bg-brand-primary/10" : "border-[color:var(--border-strong)] hover:bg-[color:var(--surface-elev)]"}`}
                    data-testid={`order-status-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            {selected.notes && (
              <div className="mt-6">
                <div className="overline mb-2">Notes from customer</div>
                <p className="text-[13.5px] leading-relaxed whitespace-pre-wrap">{selected.notes}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-[13.5px] text-[color:var(--text-muted)]">Select an order to view details.</div>
        )}
      </div>
    </div>
  );
}

// ---------------- Blog ----------------
function BlogPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", slug: "", excerpt: "", content: "", category: "Design", cover_image: "", published: true });
  const load = () => api.get("/blog?published_only=false").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.title || !form.slug) return toast.error("Title and slug required");
    try { await api.post("/blog", form); toast.success("Post created"); setForm({ title: "", slug: "", excerpt: "", content: "", category: "Design", cover_image: "", published: true }); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/blog/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 md:col-span-5 card-surface p-6">
        <div className="overline mb-4">New Post</div>
        <div className="space-y-3">
          <Input placeholder="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="blog-title" />
          <Input placeholder="Slug (url-friendly)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testId="blog-slug" />
          <Input placeholder="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} testId="blog-category" />
          <Input placeholder="Cover image URL" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} testId="blog-cover" />
          <textarea rows={2} placeholder="Excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                    className="w-full rounded-xl border border-[color:var(--border-strong)] bg-transparent p-3 text-[13.5px]" data-testid="blog-excerpt" />
          <textarea rows={6} placeholder="Content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                    className="w-full rounded-xl border border-[color:var(--border-strong)] bg-transparent p-3 text-[13.5px]" data-testid="blog-content" />
          <button onClick={save} className="h-10 px-4 rounded-full bg-brand-primary text-white text-[13px] font-semibold inline-flex items-center gap-1.5" data-testid="blog-save">
            <Plus size={14} /> Publish
          </button>
        </div>
      </div>
      <div className="col-span-12 md:col-span-7 card-surface p-4 max-h-[70vh] overflow-auto">
        {items.map((p) => (
          <div key={p.id} className="p-4 rounded-xl mb-1 flex items-start justify-between gap-4 hover:bg-[color:var(--surface-elev)]">
            <div>
              <div className="font-medium text-[14px]">{p.title}</div>
              <div className="text-[12px] text-[color:var(--text-muted)]">{p.category} · /{p.slug}</div>
            </div>
            <button onClick={() => del(p.id)} className="text-red-500 h-8 w-8 rounded-lg border border-red-500/30 grid place-items-center hover:bg-red-500/10"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Portfolio ----------------
function PortfolioPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", subtitle: "", description: "", kind: "client", category: "Website", industry: "", cover_image: "", tags: [] });
  const load = () => api.get("/portfolio").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.title) return toast.error("Title required");
    try { await api.post("/portfolio", form); toast.success("Added"); setForm({ title: "", subtitle: "", description: "", kind: "client", category: "Website", industry: "", cover_image: "", tags: [] }); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/portfolio/${id}`); load(); };
  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 md:col-span-5 card-surface p-6 space-y-3">
        <div className="overline">New Project</div>
        <Input placeholder="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="pf-title" />
        <Input placeholder="Subtitle" value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} testId="pf-subtitle" />
        <Input placeholder="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="pf-desc" />
        <div className="grid grid-cols-2 gap-2">
          <SelectMini value={form.kind} onChange={(v) => setForm({ ...form, kind: v })} opts={["client", "product"]} />
          <SelectMini value={form.category} onChange={(v) => setForm({ ...form, category: v })} opts={["Website", "E-Commerce", "Software", "Automation", "Business Systems"]} />
        </div>
        <Input placeholder="Industry" value={form.industry} onChange={(v) => setForm({ ...form, industry: v })} testId="pf-industry" />
        <Input placeholder="Cover image URL" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} testId="pf-cover" />
        <button onClick={save} className="h-10 px-4 rounded-full bg-brand-primary text-white text-[13px] font-semibold inline-flex items-center gap-1.5" data-testid="pf-save">
          <Plus size={14} /> Add project
        </button>
      </div>
      <div className="col-span-12 md:col-span-7 card-surface p-4 max-h-[70vh] overflow-auto">
        {items.map((p) => (
          <div key={p.id} className="p-4 rounded-xl mb-1 flex items-center justify-between hover:bg-[color:var(--surface-elev)]">
            <div>
              <div className="font-medium text-[14px]">{p.title}</div>
              <div className="text-[12px] text-[color:var(--text-muted)]">{p.kind} · {p.category}</div>
            </div>
            <button onClick={() => del(p.id)} className="text-red-500 h-8 w-8 rounded-lg border border-red-500/30 grid place-items-center hover:bg-red-500/10"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Testimonials ----------------
function TestimonialsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", role: "", company: "", quote: "", rating: 5 });
  const load = () => api.get("/testimonials").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.post("/testimonials", form); setForm({ name: "", role: "", company: "", quote: "", rating: 5 }); load(); toast.success("Added"); } catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/testimonials/${id}`); load(); };
  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 md:col-span-5 card-surface p-6 space-y-3">
        <div className="overline">New Testimonial</div>
        <Input placeholder="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="t-name" />
        <Input placeholder="Role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} testId="t-role" />
        <Input placeholder="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testId="t-company" />
        <textarea rows={4} placeholder="Quote" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })}
                  className="w-full rounded-xl border border-[color:var(--border-strong)] bg-transparent p-3 text-[13.5px]" data-testid="t-quote" />
        <button onClick={save} className="h-10 px-4 rounded-full bg-brand-primary text-white text-[13px] font-semibold" data-testid="t-save">Add</button>
      </div>
      <div className="col-span-12 md:col-span-7 card-surface p-4 max-h-[70vh] overflow-auto">
        {items.map((t) => (
          <div key={t.id} className="p-4 rounded-xl mb-1 flex items-start justify-between gap-4 hover:bg-[color:var(--surface-elev)]">
            <div className="flex-1">
              <div className="text-[13.5px]">"{t.quote}"</div>
              <div className="text-[12px] text-[color:var(--text-muted)] mt-1">{t.name} · {t.role}, {t.company}</div>
            </div>
            <button onClick={() => del(t.id)} className="text-red-500 h-8 w-8 rounded-lg border border-red-500/30 grid place-items-center"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- FAQs ----------------
function FaqsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ question: "", answer: "", category: "General", order: 100 });
  const load = () => api.get("/faqs").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.post("/faqs", form); setForm({ question: "", answer: "", category: "General", order: 100 }); load(); toast.success("Added"); } catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/faqs/${id}`); load(); };
  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 md:col-span-5 card-surface p-6 space-y-3">
        <div className="overline">New FAQ</div>
        <Input placeholder="Question" value={form.question} onChange={(v) => setForm({ ...form, question: v })} testId="f-question" />
        <textarea rows={5} placeholder="Answer" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })}
                  className="w-full rounded-xl border border-[color:var(--border-strong)] bg-transparent p-3 text-[13.5px]" data-testid="f-answer" />
        <button onClick={save} className="h-10 px-4 rounded-full bg-brand-primary text-white text-[13px] font-semibold" data-testid="f-save">Add</button>
      </div>
      <div className="col-span-12 md:col-span-7 card-surface p-4 max-h-[70vh] overflow-auto">
        {items.map((f) => (
          <div key={f.id} className="p-4 rounded-xl mb-1 flex items-start justify-between gap-4 hover:bg-[color:var(--surface-elev)]">
            <div className="flex-1">
              <div className="text-[13.5px] font-medium">{f.question}</div>
              <div className="text-[12px] text-[color:var(--text-muted)] mt-1">{f.answer}</div>
            </div>
            <button onClick={() => del(f.id)} className="text-red-500 h-8 w-8 rounded-lg border border-red-500/30 grid place-items-center"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Input({ placeholder, value, onChange, testId }) {
  return (
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
           className="w-full h-11 rounded-xl border border-[color:var(--border-strong)] bg-transparent px-3.5 text-[13.5px] focus:border-brand-primary transition-colors"
           data-testid={testId} />
  );
}

function SelectMini({ value, onChange, opts }) {
  return (
    <div className="flex flex-wrap gap-1">
      {opts.map((o) => (
        <button key={o} onClick={() => onChange(o)}
                className={`h-9 px-3 rounded-lg text-[12px] font-medium border transition-colors ${value === o ? "border-brand-primary bg-brand-primary/10" : "border-[color:var(--border-strong)]"}`}>
          {o}
        </button>
      ))}
    </div>
  );
}
